// contexts/ConfirmContext.tsx
'use client'

import { createContext, useContext, useState, ReactNode } from 'react'
import ConfirmDialog from 'popup/ConfirmDialog'

interface ConfirmOption {
    message: string
    value: number
    className?: string
}

interface ConfirmContextType {
    confirm: (options: ConfirmOptions) => Promise<boolean | number[]>
}

interface ConfirmOptions {
    title: string
    description?: string
    options?: any[] | null
}

const ConfirmContext = createContext<ConfirmContextType>({} as ConfirmContextType)

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false)
    const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({ title: '' })
    const [resolveFn, setResolveFn] = useState<(value: boolean | number[]) => void>()

    const confirm = (options: ConfirmOptions): Promise<boolean | number[]> => {
        setConfirmOptions(options)
        setOpen(true)
        return new Promise((resolve) => {
            setResolveFn(() => resolve)
        })
    }

    const handleClose = () => {
        setOpen(false)
        resolveFn?.(false)
    }

    const handleConfirm = (values?: number[]) => {
        setOpen(false)
        if (values !== undefined) {
            resolveFn?.(values)
        } else {
            resolveFn?.(true)
        }
    }

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            <ConfirmDialog
                isOpen={open}
                title={confirmOptions.title}
                description={confirmOptions.description}
                options={confirmOptions.options}
                onClose={handleClose}
                onConfirm={handleConfirm}
            />
        </ConfirmContext.Provider>
    )
}

export const useConfirm = () => useContext(ConfirmContext)